"use client";

import { useRefs, type Refs } from "@mediamonks/react-kit";
import {
  MouseEvent,
  type ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import classes from "./C100Navigation.module.scss";
import {
  AppLogo,
  HorizontalNavigation,
  NavigationMenuGroup,
  NavigationMenuItem,
  NavigationQuickActionGroup,
  VerticalNavigation,
} from "@hawkins/components";
import { M1Button, M1ButtonProps } from "../../molecules/M1Button/M1Button.tsx";
import { useViewportStateMedium } from "../../../hooks/useViewportState.ts";
import { Clickable } from "../../abstract/Clickable/Clickable.tsx";
import { useLenis } from "lenis/react";
import { useRouter } from "@tanstack/react-router";

export type C100NavigationProps = {
  pageName: ReactNode;
  currentLocale: string;
  links?: Array<M1ButtonProps>;
  callToAction: M1ButtonProps;
};

export type C100NavigationRefs = Refs<{
  self: HTMLDivElement;
}>;

export function C100Navigation({
  pageName,
  currentLocale,
  links = [],
  callToAction,
}: C100NavigationProps): ReactElement {
  const refs = useRefs<C100NavigationRefs>();
  const lenis = useLenis();
  const router = useRouter();

  const isMediumViewport = useViewportStateMedium();
  const [mobileNavigationIsVisible, setMobileNavigationIsVisible] =
    useState(false);

  const toggleMobileNavigation = useCallback(() => {
    setMobileNavigationIsVisible((prev) => !prev);
  }, []);

  const onNavigationItemClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, href: string) => {
      const url = new URL(href, window.location.origin);

      const formattedRouterStatePathname =
        router.state.location.pathname.endsWith("/")
          ? router.state.location.pathname
          : `${router.state.location.pathname}/`;

      if (url.pathname !== formattedRouterStatePathname) {
        return;
      }

      event.preventDefault();

      if (url.hash) {
        setMobileNavigationIsVisible(false);

        setTimeout(() => {
          lenis?.scrollTo(url.hash);
        }, 100);
      }
    },
    [lenis, router],
  );

  const onLogoClick = useCallback(() => {
    if (router.state.location.pathname !== `/${currentLocale.toLowerCase()}`) {
      router.navigate({ to: `/${currentLocale.toLowerCase()}` });
    }
    lenis?.scrollTo(0);
  }, [currentLocale, lenis, router]);

  useEffect(() => {
    if (isMediumViewport) {
      setMobileNavigationIsVisible(false);
    }
  }, [isMediumViewport]);

  useEffect(() => {
    if (mobileNavigationIsVisible) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
  }, [mobileNavigationIsVisible, lenis]);

  const callToActionGroup = useMemo(
    () => (
      <NavigationQuickActionGroup>
        {isMediumViewport ? (
          <M1Button variant="primary" size="medium" {...callToAction} />
        ) : (
          <Clickable
            className={classes.navigationToggleButton}
            onClick={toggleMobileNavigation}
            data-is-visible={mobileNavigationIsVisible}
          >
            <span />
          </Clickable>
        )}
      </NavigationQuickActionGroup>
    ),
    [
      callToAction,
      isMediumViewport,
      mobileNavigationIsVisible,
      toggleMobileNavigation,
    ],
  );

  const navigationItemsGroup = useMemo(
    () =>
      isMediumViewport ? (
        <NavigationMenuGroup>
          {links.map((link) => (
            <NavigationMenuItem
              key={link.href}
              href={link.href}
              onClick={(event: MouseEvent<HTMLAnchorElement>) =>
                onNavigationItemClick(event, link.href ?? "#")
              }
            >
              {link.children}
            </NavigationMenuItem>
          ))}
        </NavigationMenuGroup>
      ) : (
        links.map((link) => (
          // @ts-expect-error This is a valid "onClick" prop for the M1Button component
          <M1Button
            key={link.href}
            variant="link"
            size="large"
            href={link.href}
            onClick={(event: MouseEvent<HTMLAnchorElement>) =>
              onNavigationItemClick(event, link.href ?? "#")
            }
          >
            {link.children}
          </M1Button>
        ))
      ),
    [isMediumViewport, links, onNavigationItemClick],
  );

  return (
    <>
      <HorizontalNavigation
        className={classes.c100Navigation}
        ref={refs.self}
        logo={
          <Clickable className={classes.logo} onClick={onLogoClick}>
            <AppLogo
              name={<>{pageName}</>}
              classes={{ textContainer: classes.textContainer }}
              studioLogo
            />
          </Clickable>
        }
        quickActions={callToActionGroup}
      >
        {isMediumViewport && navigationItemsGroup}
      </HorizontalNavigation>
      {mobileNavigationIsVisible && (
        <VerticalNavigation
          key={Date.now()}
          className={classes.mobileNavigation}
          classes={{
            childrenContainer: classes.mobileNavigationChildrenContainer,
            mainContent: classes.mobileNavigationMainContent,
          }}
        >
          {navigationItemsGroup}
        </VerticalNavigation>
      )}
    </>
  );
}
